import dayjs from 'dayjs';
import curry from 'lodash/curry';

import { HOURS } from 'Components/VehicleList/SaleTime/saleTimeConsts';

import type {
	ConvertMilliseconds,
	CustomDate,
	DateToMove,
	Direction,
	GetNextSaleDayProps,
	GetSaleTimeProps,
} from './dateHelpers.types';

export const TIME_IN_MS = {
	day: 86400000,
	hour: 3600000,
	minute: 60000,
	second: 1000,
};

export const millisecondsBetween = (date1: CustomDate, date2: CustomDate): number => dayjs(date2).diff(date1);

const moveDate = curry(
	(direction: Direction, { date = new Date(), days, hours, milliseconds = 0, minutes, seconds = 0 }: DateToMove) => {
		const duration = dayjs.duration({
			days,
			hours,
			milliseconds,
			minutes,
			seconds,
		}) as unknown as number;
		const newDate =
			direction === 'forward'
				? dayjs(date).add(duration, 'milliseconds')
				: dayjs(date).subtract(duration, 'milliseconds');
		return {
			date: newDate,
			milliseconds: direction === 'forward' ? millisecondsBetween(date, newDate) : millisecondsBetween(newDate, date),
		};
	},
);

export const addToDate = moveDate('forward');
export const subtractFromDate = moveDate('backwards');

export const periodMatcher = curry(
	(millisecondsRemaining: number, period: { end: number; start: number }) =>
		millisecondsRemaining <= period.start && millisecondsRemaining >= period.end,
);

export const getNextSaleDay = ({ nextSaleStartsAt }: GetNextSaleDayProps): number => dayjs(nextSaleStartsAt).day();

export const convertMilliseconds = (milliseconds: number): ConvertMilliseconds => {
	let hours;
	let minutes;
	let seconds;
	seconds = Math.floor(milliseconds / 1000);
	minutes = Math.floor(seconds / 60);
	seconds %= 60;
	hours = Math.floor(minutes / 60);
	minutes %= 60;
	const days = Math.floor(hours / 24);
	hours %= 24;
	return {
		days,
		hours,
		minutes,
		seconds,
	};
};

export const isZeroMinutes = (milliseconds: number): boolean => {
	const { minutes } = convertMilliseconds(milliseconds);

	return minutes === 0;
};

export const formatWithoutZeroMinutes = (milliseconds: number, defaultFormat: string): unknown =>
	isZeroMinutes(milliseconds) ? HOURS : defaultFormat;

export const formatMilliseconds = (milliseconds: number, format: string): string =>
	dayjs.duration(convertMilliseconds(milliseconds)).format(format);

export const getSaleTime = ({
	calculateFormat,
	defaultFormat,
	millisecondsRemaining,
	periods,
}: GetSaleTimeProps): string => {
	const currentPeriod = periods.find(periodMatcher(millisecondsRemaining));
	if (currentPeriod) {
		return formatMilliseconds(millisecondsRemaining, currentPeriod.format);
	}

	const format = calculateFormat?.(millisecondsRemaining, defaultFormat) || defaultFormat;
	return formatMilliseconds(millisecondsRemaining, format);
};
